import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import CourseExamPlayer from '../../../admin/academyTabs/courseExam/CourseExamPlayer';

function abandonExamAttempt(attemptId) {
  if (!attemptId) return;
  const payload = JSON.stringify({ attemptId });

  fetch('/api/user/academy/abandon-course-exam', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    keepalive: true,
    body: payload,
  }).catch(() => {});
}

export default function MemberCourseExamModal({ show, course, onClose, onExamFinished }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const attemptIdRef = useRef(null);
  const examActiveRef = useRef(false);

  const portalTarget =
    typeof window !== 'undefined' && window.document
      ? window.document.body || window.document.getElementById('root')
      : null;

  const handleClose = useCallback(() => {
    if (examActiveRef.current && attemptIdRef.current) {
      abandonExamAttempt(attemptIdRef.current);
    }
    examActiveRef.current = false;
    attemptIdRef.current = null;
    setSession(null);
    setError('');
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.document?.body) return undefined;
    if (show) window.document.body.classList.add('modal-open');
    else window.document.body.classList.remove('modal-open');
    return () => window.document.body.classList.remove('modal-open');
  }, [show]);

  useEffect(() => {
    if (!show || !course?.id) {
      setSession(null);
      setError('');
      setLoading(false);
      examActiveRef.current = false;
      attemptIdRef.current = null;
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    axios
      .post(
        '/api/user/academy/start-course-exam',
        { courseId: course.id },
        { withCredentials: true }
      )
      .then((response) => {
        if (cancelled) return;
        const data = response.data?.data;
        if (!data?.exam || !data?.attemptId) {
          setError('Unable to start the examination.');
          setSession(null);
          return;
        }

        attemptIdRef.current = data.attemptId;
        examActiveRef.current = true;
        setSession({
          attemptId: data.attemptId,
          attemptNumber: data.attemptNumber,
          attemptsMax: data.attemptsMax,
          handleId: data.handleId,
          passPercentThreshold: data.passPercentThreshold ?? 70,
          exam: {
            ...data.exam,
            courseId: data.exam.courseId ?? course.id,
            handleId: data.handleId,
            passPercentThreshold: data.passPercentThreshold ?? 70,
          },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to start member course exam', err);
        setError(err.response?.data?.error || 'Unable to start the examination.');
        setSession(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, course?.id]);

  useEffect(() => {
    if (!show || !session?.attemptId) return undefined;

    const onBeforeUnload = () => {
      abandonExamAttempt(session.attemptId);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [show, session?.attemptId]);

  if (!show || !course || !portalTarget) return null;

  const courseTitle = course.displayName || course.courseName || 'Course';

  return ReactDOM.createPortal(
    <div
      className="course-exam-modal-overlay"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="course-exam-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="member-course-exam-modal-title"
      >
        <header className="course-exam-modal__header">
          <div className="course-exam-modal__header-main">
            <p className="course-exam-modal__eyebrow">Examination room</p>
            <h2 id="member-course-exam-modal-title" className="course-exam-modal__title">
              {courseTitle}
            </h2>
          </div>

          <div className="course-exam-modal__header-badges">
            {session?.attemptNumber ? (
              <span className="course-exam-modal__badge">
                {session.attemptsMax === 0
                  ? `Attempt ${session.attemptNumber}`
                  : `Attempt ${session.attemptNumber} / ${session.attemptsMax}`}
              </span>
            ) : null}
          </div>

          <button
            type="button"
            className="course-exam-modal__close"
            onClick={handleClose}
            aria-label="Close"
          />
        </header>

        <div className="course-exam-modal__body">
          {loading ? <em>Loading examination…</em> : null}
          {!loading && error ? <p className="error">{error}</p> : null}
          {!loading && !error && session ? (
            <CourseExamPlayer
              exam={session.exam}
              memberExamMode
              persistCompletions
              attemptId={session.attemptId}
              passPercentThreshold={session.passPercentThreshold}
              onExamFinished={() => {
                examActiveRef.current = false;
                onExamFinished?.();
              }}
            />
          ) : null}
        </div>
      </div>
    </div>,
    portalTarget
  );
}
